import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { cn } from '../lib/utils';
import api, { clearCache } from '../lib/api';
import { usePushNotifications } from '../hooks/usePushNotifications';
import {
  ConversationList,
  ChatWindow,
  NewConversationModal,
  DeleteConfirmModal
} from '../components/messages';

export default function Messages() {
  const { user } = useAuth();
  const { theme } = useTheme();
  
  // State
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [conversationType, setConversationType] = useState('direct');
  const [mobileView, setMobileView] = useState('list');
  const [typingUsers, setTypingUsers] = useState([]);
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Refs
  const pollingRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingRef = useRef(null);
  const lastFetchRef = useRef({ conversations: 0, messages: 0 });
  
  // Hooks
  const { 
    isSupported: pushSupported, 
    isSubscribed: pushSubscribed, 
    loading: pushLoading, 
    subscribe: subscribePush, 
    unsubscribe: unsubscribePush,
    sendTestNotification 
  } = usePushNotifications();

  const isDark = theme === 'dark';

  // Optimized API Functions with caching
  const fetchConversations = useCallback(async (force = false) => {
    const now = Date.now();
    // Skip if fetched recently (within 500ms) unless forced
    if (!force && now - lastFetchRef.current.conversations < 500) return;
    lastFetchRef.current.conversations = now;
    
    try {
      const response = await api.get('/messages/conversations');
      setConversations(response.data);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAllUsers = useCallback(async () => {
    try {
      const response = await api.get('/messages/users/search?q=');
      setAllUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, []);

  const fetchMessages = useCallback(async (conversationId, silent = false) => {
    const now = Date.now();
    // Skip if fetched recently (within 300ms)
    if (now - lastFetchRef.current.messages < 300) return;
    lastFetchRef.current.messages = now;
    
    try {
      const response = await api.get(`/messages/conversations/${conversationId}/messages`);
      setMessages(response.data);
      if (!silent) fetchConversations(true);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  }, [fetchConversations]);

  const fetchTypingStatus = useCallback(async (conversationId) => {
    try {
      const response = await api.get(`/messages/conversations/${conversationId}/typing`);
      setTypingUsers(response.data.typing || []);
    } catch (error) {}
  }, []);

  // Effects - Optimized polling
  useEffect(() => {
    fetchConversations(true);
    fetchAllUsers();
    
    // Faster polling for messages (500ms), slower for conversations (2s)
    let messagePolling = null;
    let conversationPolling = null;
    
    conversationPolling = setInterval(() => {
      fetchConversations();
    }, 2000);
    
    pollingRef.current = { messagePolling, conversationPolling };
    
    return () => {
      if (pollingRef.current?.messagePolling) clearInterval(pollingRef.current.messagePolling);
      if (pollingRef.current?.conversationPolling) clearInterval(pollingRef.current.conversationPolling);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, [fetchConversations, fetchAllUsers]);

  // Fast message polling when conversation is selected
  useEffect(() => {
    if (pollingRef.current?.messagePolling) {
      clearInterval(pollingRef.current.messagePolling);
    }
    
    if (selectedConversation) {
      fetchMessages(selectedConversation.id);
      
      pollingRef.current.messagePolling = setInterval(() => {
        fetchMessages(selectedConversation.id, true);
        fetchTypingStatus(selectedConversation.id);
      }, 500); // 500ms for messages
    }
    
    return () => {
      if (pollingRef.current?.messagePolling) {
        clearInterval(pollingRef.current.messagePolling);
      }
    };
  }, [selectedConversation, fetchMessages, fetchTypingStatus]);

  // Handlers
  const handleToggleNotifications = useCallback(async () => {
    if (pushSubscribed) {
      const success = await unsubscribePush();
      if (success) toast.success('Notifications désactivées');
    } else {
      const success = await subscribePush();
      if (success) {
        toast.success('Notifications activées !');
        setTimeout(() => sendTestNotification(), 1000);
      }
    }
  }, [pushSubscribed, subscribePush, unsubscribePush, sendTestNotification]);

  const sendTypingStatus = useCallback(async () => {
    if (!selectedConversation) return;
    
    const now = Date.now();
    if (lastTypingRef.current && now - lastTypingRef.current < 2000) return;
    lastTypingRef.current = now;
    
    try {
      await api.post(`/messages/conversations/${selectedConversation.id}/typing`);
    } catch (error) {}
    
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(async () => {
      try {
        await api.delete(`/messages/conversations/${selectedConversation.id}/typing`);
      } catch (error) {}
    }, 3000);
  }, [selectedConversation]);

  const handleMessageInput = useCallback((e) => {
    setNewMessage(e.target.value);
    sendTypingStatus();
  }, [sendTypingStatus]);

  const handleSendMessage = useCallback(async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation) return;
    
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    try {
      await api.delete(`/messages/conversations/${selectedConversation.id}/typing`);
    } catch (error) {}
    
    setSendingMessage(true);
    try {
      await api.post(`/messages/conversations/${selectedConversation.id}/messages`, {
        content: newMessage.trim()
      });
      setNewMessage('');
      // Immediate refresh
      fetchMessages(selectedConversation.id);
      fetchConversations(true);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setSendingMessage(false);
    }
  }, [newMessage, selectedConversation, fetchMessages, fetchConversations]);

  const handleSelectConversation = useCallback((conv) => {
    setSelectedConversation(conv);
    setMobileView('chat');
    setSelectedMessageId(null);
  }, []);

  const handleBack = useCallback(() => {
    setMobileView('list');
    setSelectedConversation(null);
    setSelectedMessageId(null);
  }, []);

  const handleMessageClick = useCallback((msgId, isOwn, isDeleted) => {
    if (isOwn && !isDeleted) {
      setSelectedMessageId(msgId);
    } else {
      setSelectedMessageId(null);
    }
  }, []);

  const handleDeleteClick = useCallback((e, msgId) => {
    e.stopPropagation();
    setSelectedMessageId(msgId);
    setShowDeleteConfirm(true);
  }, []);

  const handleDeleteMessage = useCallback(async () => {
    if (!selectedMessageId) return;
    
    try {
      await api.delete(`/messages/messages/${selectedMessageId}`);
      setMessages(prev => prev.map(msg => {
        if (msg.id === selectedMessageId) {
          return {
            ...msg,
            deleted: true,
            deleted_by: user?.id,
            content: `${user?.prenom} a supprimé un message`
          };
        }
        return msg;
      }));
      toast.success('Message supprimé');
      setSelectedMessageId(null);
      setShowDeleteConfirm(false);
      // Clear cache and force refresh conversations to update preview
      clearCache('/messages/conversations');
      setTimeout(() => fetchConversations(true), 100);
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Erreur lors de la suppression');
    }
  }, [selectedMessageId, user, fetchConversations]);

  const handleTogglePin = useCallback(async (convId, isPinned, e) => {
    e.stopPropagation();
    try {
      if (isPinned) {
        await api.delete(`/messages/conversations/${convId}/pin`);
        toast.success('Conversation désépinglée');
      } else {
        await api.post(`/messages/conversations/${convId}/pin`);
        toast.success('Conversation épinglée');
      }
      fetchConversations(true);
    } catch (error) {
      console.error('Error toggling pin:', error);
      toast.error('Erreur lors de l\'épinglage');
    }
  }, [fetchConversations]);

  const handleToggleMute = useCallback(async (convId, isMuted, e) => {
    e.stopPropagation();
    try {
      if (isMuted) {
        await api.delete(`/messages/conversations/${convId}/mute`);
        toast.success('Notifications réactivées');
      } else {
        await api.post(`/messages/conversations/${convId}/mute`);
        toast.success('Conversation mise en sourdine');
      }
      fetchConversations(true);
    } catch (error) {
      console.error('Error toggling mute:', error);
      toast.error('Erreur');
    }
  }, [fetchConversations]);

  // New conversation handlers
  const handleTypeChange = useCallback((type) => {
    setConversationType(type);
    if (type === 'direct') setSelectedUsers([]);
  }, []);

  const handleSelectUser = useCallback((selectedUser) => {
    if (conversationType === 'direct') {
      setSelectedUsers([selectedUser]);
    } else {
      setSelectedUsers(prev => {
        if (!prev.find(u => u.id === selectedUser.id)) {
          return [...prev, selectedUser];
        }
        return prev;
      });
    }
  }, [conversationType]);

  const handleRemoveUser = useCallback((userId) => {
    setSelectedUsers(prev => prev.filter(u => u.id !== userId));
  }, []);

  const handleCreateConversation = useCallback(async () => {
    if (selectedUsers.length === 0) {
      toast.error('Sélectionnez au moins un utilisateur');
      return;
    }
    
    if (conversationType === 'group' && !groupName.trim()) {
      toast.error('Entrez un nom pour le groupe');
      return;
    }
    
    try {
      const response = await api.post('/messages/conversations', {
        type: conversationType,
        name: conversationType === 'group' ? groupName.trim() : null,
        participant_ids: selectedUsers.map(u => u.id)
      });
      
      const existingConv = conversations.find(c => c.id === response.data.id);
      
      if (existingConv) {
        setSelectedConversation(existingConv);
        toast.info('Conversation existante ouverte');
      } else {
        setConversations(prev => [response.data, ...prev]);
        setSelectedConversation(response.data);
        toast.success(conversationType === 'group' ? 'Groupe créé !' : 'Conversation créée !');
      }
      
      handleCloseNewConversation();
      setMobileView('chat');
    } catch (error) {
      console.error('Error creating conversation:', error);
      toast.error('Erreur lors de la création');
    }
  }, [selectedUsers, conversationType, groupName, conversations]);

  const handleCloseNewConversation = useCallback(() => {
    setShowNewConversation(false);
    setSelectedUsers([]);
    setGroupName('');
    setConversationType('direct');
  }, []);

  // Utility functions - memoized
  const formatTime = useCallback((dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Hier';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('fr-FR', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
    }
  }, []);

  return (
    <div className={cn(
      'h-[calc(100vh-180px)] md:h-[calc(100vh-120px)] flex rounded-xl overflow-hidden border',
      isDark ? 'bg-zinc-900/50 border-white/10' : 'bg-white border-gray-200'
    )} data-testid="messages-page">
      
      {/* Conversations List */}
      <ConversationList
        conversations={conversations}
        selectedConversation={selectedConversation}
        currentUserId={user?.id}
        isDark={isDark}
        loading={loading}
        mobileView={mobileView}
        pushSupported={pushSupported}
        pushSubscribed={pushSubscribed}
        pushLoading={pushLoading}
        onSelectConversation={handleSelectConversation}
        onNewConversation={() => setShowNewConversation(true)}
        onToggleNotifications={handleToggleNotifications}
        onTogglePin={handleTogglePin}
        onToggleMute={handleToggleMute}
        formatTime={formatTime}
      />
      
      {/* Chat Window */}
      <ChatWindow
        conversation={selectedConversation}
        messages={messages}
        newMessage={newMessage}
        sendingMessage={sendingMessage}
        typingUsers={typingUsers}
        selectedMessageId={selectedMessageId}
        currentUserId={user?.id}
        isDark={isDark}
        mobileView={mobileView}
        onBack={handleBack}
        onMessageChange={handleMessageInput}
        onSendMessage={handleSendMessage}
        onMessageClick={handleMessageClick}
        onDeleteClick={handleDeleteClick}
        formatTime={formatTime}
      />
      
      {/* New Conversation Modal */}
      <NewConversationModal
        isOpen={showNewConversation}
        isDark={isDark}
        conversationType={conversationType}
        groupName={groupName}
        allUsers={allUsers}
        selectedUsers={selectedUsers}
        onClose={handleCloseNewConversation}
        onTypeChange={handleTypeChange}
        onGroupNameChange={setGroupName}
        onSelectUser={handleSelectUser}
        onRemoveUser={handleRemoveUser}
        onCreate={handleCreateConversation}
      />
      
      {/* Delete Confirm Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteConfirm}
        isDark={isDark}
        onClose={() => {
          setShowDeleteConfirm(false);
          setSelectedMessageId(null);
        }}
        onConfirm={handleDeleteMessage}
      />
    </div>
  );
}
